/**
 * API helper functions for Compute Jobs feature (SP-5767)
 */

import { apiPrefix } from '../../utils/functions';
import {
  Container,
  ListContainersResponse,
  SubmitJobRequest,
  SubmitJobResponse,
  ComputeJob,
  ListJobsResponse,
} from '../../components/compute-jobs/types';

/**
 * Fetch available container images from the backend
 *
 * @returns Promise<Container[]> - Array of available containers
 * @throws Error if the API request fails
 */
export const fetchAvailableContainers = async (): Promise<Container[]> => {
  try {
    const response = await fetch(apiPrefix() + '/v1/compute/list_available_containers', {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('Unauthorized: User not logged in or session expired');
        throw new Error('Authentication required. Please log in.');
      }

      try {
        const errorData = await response.json();
        console.error('Fetch containers error response:', errorData);
        throw new Error(
          errorData.message || errorData.detail || 'Failed to fetch available containers',
        );
      } catch (parseError) {
        throw new Error(`Failed to fetch available containers (HTTP ${response.status})`);
      }
    }

    const data: ListContainersResponse = await response.json();
    return data.containers;
  } catch (error) {
    console.error('Error fetching available containers:', error);
    throw error;
  }
};

/**
 * Submit a new compute job to PanDA
 *
 * @param jobRequest - Job submission payload
 * @returns Promise<SubmitJobResponse> - Response with job ID
 * @throws Error if the API request fails
 */
export const submitComputeJob = async (
  jobRequest: SubmitJobRequest,
): Promise<SubmitJobResponse> => {
  try {
    console.log('Submitting job with payload:', jobRequest);
    const response = await fetch(apiPrefix() + '/v1/compute/submit_job', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(jobRequest),
    });

    console.log('Submit job response status:', response.status);

    if (!response.ok) {
      try {
        const errorData = await response.json();
        console.error('Submit job error response:', errorData);

        if (response.status === 422) {
          if (errorData.detail && Array.isArray(errorData.detail)) {
            const validationErrors = errorData.detail
              .map((err: any) => `${err.loc?.join('.') || 'field'}: ${err.msg}`)
              .join(', ');
            throw new Error(`Validation error: ${validationErrors}`);
          }
        }

        throw new Error(
          errorData.message || errorData.detail || 'Failed to submit compute job',
        );
      } catch (parseError) {
        throw new Error(`Failed to submit compute job (HTTP ${response.status})`);
      }
    }

    const result: SubmitJobResponse = await response.json();
    console.log('Submit job success response:', result);
    return result;
  } catch (error) {
    console.error('Error submitting compute job:', error);
    throw error;
  }
};

/**
 * Fetch list of compute jobs from the backend
 *
 * @param limit - Maximum number of jobs to return (default: 20)
 * @param offset - Number of jobs to skip (default: 0)
 * @returns Promise<ComputeJob[]> - Array of compute jobs
 * @throws Error if the API request fails
 */
export const fetchComputeJobs = async (
  limit: number = 20,
  offset: number = 0,
): Promise<ComputeJob[]> => {
  try {
    const response = await fetch(
      apiPrefix() + `/v1/compute/list_jobs?limit=${limit}&offset=${offset}`,
      {
        method: 'GET',
        credentials: 'include',
      },
    );

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('Unauthorized: User not logged in or session expired');
        throw new Error('Authentication required. Please log in.');
      }

      try {
        const errorData = await response.json();
        console.error('Fetch compute jobs error response:', errorData);
        throw new Error(
          errorData.message || errorData.detail || 'Failed to fetch compute jobs',
        );
      } catch (parseError) {
        throw new Error(`Failed to fetch compute jobs (HTTP ${response.status})`);
      }
    }

    const data: ListJobsResponse = await response.json();
    return data.jobs_list;
  } catch (error) {
    console.error('Error fetching compute jobs:', error);
    throw error;
  }
};
